import { useEffect, useState } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { userSession } from '../utils/userSession.js';
import ApiAccessRequest from './ApiAccessRequest.js';

const API_BASE = 'https://api.driftcal.net';
const META_URL = `${API_BASE}/v1/meta`;
const EVENTS_URL = `${API_BASE}/v1/events`;
const CONTACT_EMAIL = 'contact@shopadv.net';

const SECTIONS = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'authorisation', label: 'Authorisation' },
  { id: 'meta', label: 'Meta' },
  { id: 'events', label: 'Events' }
];

const FILTERS = [
  {
    name: 'country',
    required: 'Yes',
    detail: '2-letter code only: AU, NZ, or US. Get the list from GET /v1/meta.'
  },
  {
    name: 'state',
    required: 'No',
    detail: 'Short state code only (NSW, OH, WK). Multiple allowed, comma-separated: NSW,VIC. Get the list from GET /v1/meta.'
  },
  {
    name: 'from',
    required: 'No',
    detail: 'Only events that start on or after this date or datetime.'
  },
  {
    name: 'to',
    required: 'No',
    detail: 'Only events that start on or before this date or datetime.'
  },
  {
    name: 'updated_since',
    required: 'No',
    detail: 'Only events changed at or after this timestamp. Useful for incremental sync.'
  },
  {
    name: 'historic',
    required: 'No',
    detail: "'true' includes past events. Default is 'false' — upcoming only (StartDateTime from today)."
  },
  {
    name: 'limit',
    required: 'No',
    detail: 'Maximum rows to return. Default 500, maximum 1000.'
  }
];

const FIELDS = [
  { object: 'event', fields: 'EventID, EventName, EventType, RoundNum, EventLink, StartDateTime, EndDateTime, updated_at' },
  { object: 'country', fields: 'CountryID, CountryName, CountryCode' },
  { object: 'state', fields: 'StateID, StateNameShort, StateNameLong, GMT' },
  { object: 'organiser', fields: 'OrganiserID, BusinessName, WebsiteLink, organiserImage' },
  { object: 'venue', fields: 'VenueID, VenueName, City, AddressLine, latitude, longitude' },
  { object: 'layout', fields: 'LayoutID, LayoutName' }
];

const ERRORS = [
  { status: '200', detail: 'Success. Events: { count, historic, events }. Meta: { count, countries }.' },
  { status: '400', detail: 'Missing API key.' },
  { status: '401', detail: 'Invalid API key.' },
  { status: '402', detail: 'Origin is not allowed for this API key.' },
  { status: '403', detail: 'Max daily requests reached (max 24, resetting in X minutes).' },
  { status: '404', detail: 'Missing country.' },
  { status: '405', detail: 'Invalid country. Must be AU, NZ, or US.' },
  { status: '406', detail: 'Invalid state. Short codes only (NSW, OH, WK). Multiple: NSW,VIC.' },
  { status: '407', detail: 'Invalid from date/datetime.' },
  { status: '408', detail: 'Invalid to date/datetime.' },
  { status: '409', detail: 'Invalid updated_since date/datetime.' },
  { status: '410', detail: 'Invalid historic value. Must be true, false, or empty.' },
  { status: '411', detail: 'Invalid limit value. Must be 1000 or less.' },
  { status: '500', detail: 'Something went wrong on our side. Try again later.' }
];

const EXAMPLE_RESPONSE = `{
  "count": 1,
  "historic": false,
  "events": [
    {
      "EventID": 123,
      "EventName": "Event name",
      "EventType": "Competition",
      "RoundNum": 4,
      "EventLink": "https://...",
      "StartDateTime": "2026-09-12T09:00:00",
      "EndDateTime": "2026-09-12T17:00:00",
      "updated_at": "2026-09-07T00:00:00",
      "country": {
        "CountryID": 1,
        "CountryName": "Australia",
        "CountryCode": "AU"
      },
      "state": {
        "StateID": 2,
        "StateNameShort": "NSW",
        "StateNameLong": "New South Wales",
        "GMT": "+10"
      },
      "organiser": {
        "OrganiserID": 9,
        "BusinessName": "Australian Drift Club",
        "WebsiteLink": "https://...",
        "organiserImage": "https://..."
      },
      "venue": {
        "VenueID": 4,
        "VenueName": "Sydney Motorsport Park",
        "City": "Eastern Creek",
        "AddressLine": null,
        "latitude": null,
        "longitude": null
      },
      "layout": {
        "LayoutID": 2,
        "LayoutName": "Gardner GP"
      }
    }
  ]
}`;

const META_FIELDS = [
  { object: 'country', fields: 'CountryID, CountryName, CountryCode, states[]' },
  { object: 'state', fields: 'StateID, StateNameShort, StateNameLong, GMT' }
];

const META_EXAMPLE = `{
  "count": 3,
  "countries": [
    {
      "CountryID": 1,
      "CountryName": "Australia",
      "CountryCode": "AU",
      "states": [
        {
          "StateID": 2,
          "StateNameShort": "NSW",
          "StateNameLong": "New South Wales",
          "GMT": "+10"
        },
        {
          "StateID": 7,
          "StateNameShort": "VIC",
          "StateNameLong": "Victoria",
          "GMT": "+10"
        }
      ]
    }
  ]
}`;

const META_CURL = `curl -H "x-api-key: YOUR_KEY" "${META_URL}"`;
const CURL_EXAMPLE = `curl -H "x-api-key: YOUR_KEY" "${EVENTS_URL}?country=AU&limit=5"`;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function CodeBlock({ children }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-900 px-4 py-3 text-xs leading-5 text-slate-100">
      <code>{children}</code>
    </pre>
  );
}

function Introduction({ onOpenLogin, onOpenDashboard }) {
  const [user, setUser] = useState(() => userSession.getUser());

  useEffect(() => userSession.subscribe(setUser), []);

  return (
    <article>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Calendar API
      </p>
      <h1 className="mt-1 text-3xl font-bold text-slate-900">Introduction</h1>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        The DriftCal Calendar API is a read-only feed of drifting events. Use it to
        show upcoming events on your own site or app. 
      </p>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Access is keyed to a DriftCal user profile. Sign in (or create an account),
        apply with the website origin that will call the API, we will email the
        key to the address on your account if the request is approved.
      </p>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Your key</h2>
      <ul className="mt-3 list-disc pl-5 text-sm leading-6 text-slate-600">
        <li>
          <strong className="font-medium text-slate-800">Apply</strong>: Expand the
          form below. One pending application per origin. If that origin already has
          an active key, rotate it or delete it and apply again.
        </li>
      </ul>

      <div className="mt-4">
        <Disclosure>
          {({ open }) => (
            <div className="rounded-xl border border-slate-200">
              <DisclosureButton className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-900 hover:bg-slate-50">
                <span>Apply for an API key</span>
                <ChevronDownIcon
                  className={classNames(
                    'h-5 w-5 text-slate-500 transition-transform',
                    open ? 'rotate-180' : ''
                  )}
                />
              </DisclosureButton>
              <DisclosurePanel className="border-t border-slate-200 px-4 py-4">
                <ApiAccessRequest onOpenLogin={onOpenLogin} bare />
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      </div>

      <ul className="mt-4 list-disc pl-5 text-sm leading-6 text-slate-600">
        <li>
          <strong className="font-medium text-slate-800">Rotate or remove</strong>: After a key is issued, open Home on your dashboard. Rotate issues a new
          secret and invalidates the old one. Delete revokes the key.
        </li>
      </ul>
      {user && onOpenDashboard && (
        <button
          type="button"
          onClick={onOpenDashboard}
          className="mt-4 inline-flex items-center rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"
        >
          Open dashboard
        </button>
      )}
    </article>
  );
}

function Authorisation() {
  return (
    <article>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Calendar API
      </p>
      <h1 className="mt-1 text-3xl font-bold text-slate-900">Authorisation</h1>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        Every API request must include your API key. Keys look like{' '}
        <code>'dc_live_x'</code>. Send it as a request header. Do not put it in the
        query string or in client-side source that ships to other 
        browsers unless that browser origin is the one you applied with.
      </p>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Header</h2>
      <CodeBlock>x-api-key: YOUR_KEY</CodeBlock>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Origins</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Browser calls must come from the website origin on the key. A mismatch
        is <code>402</code>. curl and server-side requests with no{' '}
        <code>Origin</code> header are allowed.
      </p>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Limits</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Each key can make 24 requests per UTC day (about one an hour). Going
        over returns <code>403</code> and says how many minutes until the
        counter resets at midnight UTC.
      </p>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Errors</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Each problem has its own status code so you can tell what failed without
        reading the body. The JSON body is always{' '}
        <code>{'{ "error": "…" }'}</code>.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-semibold">Code</th>
              <th className="py-2 font-semibold">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {ERRORS.map((row) => (
              <tr key={row.status} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">
                    {row.status}
                  </code>
                </td>
                <td className="py-2 text-slate-600">{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function Events() {
  return (
    <article>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Calendar API
      </p>
      <h1 className="mt-1 text-3xl font-bold text-slate-900">Events</h1>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        Returns calendar events for a required country. Upcoming only by default,
        ordered by start time.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-bold tracking-wide text-emerald-800">
          GET
        </span>
        <code className="text-sm text-slate-800">{`${API_BASE}/v1/events`}</code>
      </div>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Request</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Add the <code>x-api-key</code> header and a required <code>country</code>
        code from <code>GET /v1/meta</code>, then any optional query variables.
        State short codes also come from meta. Example:
      </p>
      <CodeBlock>{CURL_EXAMPLE}</CodeBlock>

      <h3 className="mt-8 text-sm font-semibold text-slate-900">Query parameters</h3>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-semibold">Name</th>
              <th className="py-2 pr-4 font-semibold">Required</th>
              <th className="py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            {FILTERS.map((row) => (
              <tr key={row.name} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">
                    {row.name}
                  </code>
                </td>
                <td className="py-2 pr-4 text-slate-600">{row.required}</td>
                <td className="py-2 text-slate-600">{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-base font-semibold text-slate-900">Response</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Successful responses are JSON:{' '}
        <code>{'{ count, historic, events }'}</code>. Each event includes nested
        country, state, organiser, venue, and layout objects.
      </p>

      <h3 className="mt-8 text-sm font-semibold text-slate-900">Fields</h3>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-semibold">Object</th>
              <th className="py-2 font-semibold">Fields</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((row) => (
              <tr key={row.object} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4 font-medium text-slate-800">
                  <code>{row.object}</code>
                </td>
                <td className="py-2 text-slate-600">{row.fields}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mt-8 text-sm font-semibold text-slate-900">Example</h3>
      <CodeBlock>{EXAMPLE_RESPONSE}</CodeBlock>
    </article>
  );
}

function Meta() {
  return (
    <article>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Calendar API
      </p>
      <h1 className="mt-1 text-3xl font-bold text-slate-900">Meta</h1>
      <p className="mt-4 text-sm leading-6 text-slate-600">
        Full list of countries and the states in each country. Use it to build
        country and state filters, then pass those codes to{' '}
        <code>GET /v1/events</code>. Field names match the nested country and
        state objects on events.
      </p>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        This response rarely changes, should be cached locally. Meta counts toward
        the 24 requests per UTC day.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-bold tracking-wide text-emerald-800">
          GET
        </span>
        <code className="text-sm text-slate-800">{META_URL}</code>
      </div>

      <h2 className="mt-8 text-base font-semibold text-slate-900">Request</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Add the <code>x-api-key</code> header. There are no query variables,
        the response is always AU, NZ, and US with every state in each.
        Example:
      </p>
      <CodeBlock>{META_CURL}</CodeBlock>

      <h2 className="mt-10 text-base font-semibold text-slate-900">Response</h2>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Successful responses are JSON:{' '}
        <code>{'{ count, countries }'}</code>. Each country includes a{' '}
        <code>states</code> array. <code>count</code> is the number of countries
        (always 3).
      </p>

      <h3 className="mt-8 text-sm font-semibold text-slate-900">Fields</h3>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-4 font-semibold">Object</th>
              <th className="py-2 font-semibold">Fields</th>
            </tr>
          </thead>
          <tbody>
            {META_FIELDS.map((row) => (
              <tr key={row.object} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-4 font-medium text-slate-800">
                  <code>{row.object}</code>
                </td>
                <td className="py-2 text-slate-600">{row.fields}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mt-8 text-sm font-semibold text-slate-900">Example</h3>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Shortened. The live response includes every state for AU, NZ, and US.
      </p>
      <CodeBlock>{META_EXAMPLE}</CodeBlock>
    </article>
  );
}

export default function ApiDocs({ onOpenLogin, onOpenDashboard }) {
  const [section, setSection] = useState('introduction');

  return (
    <div className="relative isolate px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-6xl">
        <div className="overflow-hidden rounded-3xl bg-white shadow-xl lg:grid lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav
            aria-label="API documentation"
            className="border-b border-slate-200 bg-slate-50 px-3 py-4 lg:border-b-0 lg:border-r"
          >
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Reference
            </p>
            <ul className="mt-2 flex gap-1 overflow-x-auto lg:block lg:space-y-0.5">
              {SECTIONS.map((item) => (
                <li key={item.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setSection(item.id)}
                    className={classNames(
                      'w-full rounded-md px-3 py-2 text-left text-sm',
                      section === item.id
                        ? 'bg-white font-semibold text-slate-900 shadow-sm lg:bg-slate-200/70 lg:shadow-none'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    )}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-6 sm:p-8">
            {section === 'introduction' && (
              <Introduction
                onOpenLogin={onOpenLogin}
                onOpenDashboard={onOpenDashboard}
              />
            )}
            {section === 'authorisation' && <Authorisation />}
            {section === 'meta' && <Meta />}
            {section === 'events' && <Events />}
          </div>
        </div>
      </div>
    </div>
  );
}
